import * as React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { JsonResponse } from 'TypeGen/json-response';
import { fetchApi } from '../../services/api';
import { AgentNote } from '.';

interface Props {
  AgentNote: AgentNote;
  CloseDialog: () => void;
  Saved: (hasInstructions: boolean) => void;
}

interface InstructionsViewModel {
  AgentNoteID: number;
  Domain: string;
  BillToDeterminationInstructions: string | null;
}

interface TestViewModel {
  BillingCustomerID: number;
  BillingCustomerNumber: string | null;
  Domain: string | null;
  MatchedAgentNoteID: number | null;
  AgentNoteDomainMatches: boolean | null;
  ErrorMessage: string | null;
}

const placeholder = 'Enter instructions for the AI agent to follow when determining the Bill-To for this domain.';

const BillToInstructionsDialog = (props: Props) => {
  const [instructions, setInstructions] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [frontId, setFrontId] = React.useState('');
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<TestViewModel | null>(null);
  const [testError, setTestError] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    fetchApi(`/api/Quote/AgentNoteBillToDeterminationInstructions/${props.AgentNote.ID}`)
      .then((response: InstructionsViewModel) => {
        setInstructions(response.BillToDeterminationInstructions || '');
      })
      .catch((e) => {
        if (!e?.status) alert('Unable to load AI Bill-To instructions');
        props.CloseDialog();
      })
      .finally(() => setLoading(false));
  }, [props.AgentNote.ID]);

  const save = (value: string) => {
    const trimmed = value.trim();
    setSaving(true);

    fetchApi('/api/Quote/UpdateAgentNoteBillToDeterminationInstructions', {
      AgentNoteID: props.AgentNote.ID,
      BillToDeterminationInstructions: trimmed || null,
    }, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          props.Saved(trimmed.length > 0);
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        if (!e?.status) alert('Unable to save AI Bill-To instructions');
      })
      .finally(() => setSaving(false));
  };

  const clear = () => {
    if (!window.confirm(`Clear AI Bill-To instructions for ${props.AgentNote.Domain}?`)) {
      return;
    }

    save('');
  };

  const test = () => {
    const trimmedFrontId = frontId.trim();
    if (!/^(cnv_|msg_)/i.test(trimmedFrontId)) {
      setTestResult(null);
      setTestError('Enter a Front conversation ID (cnv_...) or message ID (msg_...).');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setTestError('');

    fetchApi('/api/Quote/TestAgentNoteBillToDeterminationInstructions', {
      FrontID: trimmedFrontId,
      AgentNoteID: props.AgentNote.ID,
      BillToDeterminationInstructions: instructions,
    }, 'POST')
      .then((response: TestViewModel) => setTestResult(response))
      .catch((e) => {
        if (!e?.status) setTestError('Unable to test AI Bill-To instructions.');
      })
      .finally(() => setTesting(false));
  };

  return (
    <Dialog title="AI Bill-To Instructions" onClose={props.CloseDialog} className="dialog-w800">
      <p className="mb-2"><strong>Domain:</strong> {props.AgentNote.Domain}</p>
      {!loading && <div className="alert alert-info mb-3" style={{ fontSize: '11px' }}>
        <strong>Writing tips:</strong> Use simple email keywords like Body, Subject, Image, From, and To.<br />
        For signatures, use the Body keyword: "If the email body contains..."<br />
        If the signature is an image, use "If the email body or image contains..."
      </div>}
      {loading && <span className="k-i-loading k-icon" style={{ fontSize: 48 }} />}
      {!loading && <TextArea
        autoFocus
        rows={8}
        value={instructions}
        placeholder={placeholder}
        style={{ width: '100%' }}
        onChange={(e) => {
          setInstructions(String(e.value ?? ''));
          setTestResult(null);
          setTestError('');
        }}
      />}
      {!loading && <>
        <hr />
        <p className="mb-2">
          <strong>Test these instructions</strong><br />
          Enter a Front conversation or message ID. The current text above is tested without saving it first.
        </p>
        <div className="d-flex">
          <Input
            value={frontId}
            placeholder="cnv_... or msg_..."
            style={{ flex: 1 }}
            onChange={(e) => {
              setFrontId(String(e.value ?? ''));
              setTestResult(null);
              setTestError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !testing) test();
            }}
          />
          <Button
            className="ml-2"
            themeColor="primary"
            fillMode="outline"
            disabled={testing || frontId.trim().length === 0}
            onClick={test}
          >
            {testing ? 'Testing...' : 'Test Bill To'}
          </Button>
        </div>
        {testError && <div className="alert alert-danger mt-3 mb-0">{testError}</div>}
        {testResult?.AgentNoteDomainMatches === false && <div className="alert alert-warning mt-3 mb-0">
          This Front item belongs to <strong>{testResult.Domain || 'an unknown domain'}</strong>, not <strong>{props.AgentNote.Domain}</strong>.
        </div>}
        {testResult?.ErrorMessage && <div className="alert alert-danger mt-3 mb-0">{testResult.ErrorMessage}</div>}
        {testResult?.AgentNoteDomainMatches !== false && !testResult?.ErrorMessage && testResult?.BillingCustomerID > 0 && <div className="alert alert-success mt-3 mb-0">
          Resolved Bill To: <strong>{testResult.BillingCustomerNumber}</strong>
          {testResult.MatchedAgentNoteID === props.AgentNote.ID
            ? ' using these instructions.'
            : testResult.MatchedAgentNoteID
              ? ` using another instruction for this domain (Agent Note #${testResult.MatchedAgentNoteID}).`
              : ' using the configured Bill To for this domain.'}
        </div>}
        {testResult?.AgentNoteDomainMatches !== false && !testResult?.ErrorMessage && testResult !== null && testResult.BillingCustomerID === 0 && <div className="alert alert-warning mt-3 mb-0">
          No Bill To was resolved for this Front item.
        </div>}
      </>}
      <DialogActionsBar layout="end">
        <Button
          themeColor="error"
          fillMode="outline"
          disabled={loading || saving || instructions.trim().length === 0}
          onClick={clear}
        >
          Clear
        </Button>
        <Button disabled={saving || testing} onClick={props.CloseDialog}>Cancel</Button>
        <Button
          themeColor="primary"
          disabled={loading || saving || testing}
          onClick={() => save(instructions)}
        >
          Save
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
}

export default BillToInstructionsDialog;
